import Box from 'carbon-react/esm/components/box';
import Typography from 'carbon-react/esm/components/typography';
import Divider from 'carbon-react/lib/components/divider';
import { Dl, Dt, Dd } from 'carbon-react/esm/components/definition-list';
import {
  mapNatureToDisplay,
  mapTypeLabelToDisplay,
  mapStatusDetailUIItem,
} from '../../../../utils/dsnMappings';
import Pill from 'carbon-react/esm/components/pill';
import { DsnDetailsData } from '../../../../types/dsn.types';
import { useResponsive } from '../../../../hooks/useResponsive';
import { formatDateString } from '../../../../utils/dateFormatters';
import { DateFormatType } from '../../../dsn-list/types/dsntable.types';

/* eslint-disable @typescript-eslint/no-deprecated -- Carbon's supported S pill size is incorrectly marked deprecated. */

const undefDataValue = 'N/A';

interface ApercuTabProps {
  data: DsnDetailsData;
}

const ApercuTab: React.FC<ApercuTabProps> = ({ data }) => {
  const { isMobile } = useResponsive();

  const dsnData: DsnDetailsData = data;
  const dataDeclarant = dsnData?.declarant;
  const dataDeclare = dsnData?.declare;
  const dataDeclaration = dsnData?.declaration;

  return (
    <Box
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      minHeight={isMobile ? 'auto' : '400px'}
      gap={isMobile ? 2 : 0}
      alignItems={isMobile ? 'initial' : 'stretch'}
    >
      {/* Colonne gauche - Déclarant et Déclaré */}
      <Box
        marginTop={isMobile ? '0' : '10px'}
        paddingTop={isMobile ? '8px' : '16px'}
        flex="1"
        borderRadius="borderRadius000"
        maxHeight={isMobile ? 'none' : '600px'}
      >
        <Box
          margin="var(--spacing000)"
          padding="var(--spacing000)"
          display="flex"
          flexDirection="column"
          borderRadius="borderRadius000"
          gap={isMobile ? 2 : 3}
          height="100%"
        >
          {/* Section Déclarant */}
          <Box
            className="tile-with-custom-radius"
            backgroundColor="var(--colorsUtilityYang100)"
            paddingTop="var(--spacing000)"
            paddingBottom="var(--spacing000)"
            paddingLeft={isMobile ? 'var(--spacing050)' : 'var(--spacing100)'}
            paddingRight={isMobile ? 'var(--spacing150)' : 'var(--spacing300)'}
            boxSizing="border-box"
            borderRadius="borderRadius050"
            pl={isMobile ? '12px' : '20px'}
            display="flex"
            flexDirection="column"
            position="relative"
            width="100%"
            flex="1"
          >
            <Typography variant={isMobile ? 'h3' : 'h2'} pt={isMobile ? '12px' : '20px'} mb={0}>
              Déclarant
            </Typography>
            <Divider type="horizontal" mt={2} mb={3} />

            <Dl dtTextAlign="left" ddTextAlign="left" mb={isMobile ? 2 : 0}>
              <Dt>Raison Sociale</Dt>
              <Dd>{dataDeclarant?.raisonSociale ?? undefDataValue}</Dd>
              <Dt>Siret</Dt>
              <Dd>{dataDeclarant?.siret ?? undefDataValue}</Dd>
            </Dl>
          </Box>

          {/* Section Déclaré */}
          <Box
            className="tile-with-custom-radius"
            backgroundColor="var(--colorsUtilityYang100)"
            paddingTop="var(--spacing000)"
            paddingBottom="var(--spacing000)"
            paddingLeft={isMobile ? 'var(--spacing050)' : 'var(--spacing100)'}
            paddingRight={isMobile ? 'var(--spacing150)' : 'var(--spacing300)'}
            boxSizing="border-box"
            borderRadius="borderRadius050"
            pl={isMobile ? '12px' : '20px'}
            display="flex"
            flexDirection="column"
            position="relative"
            width="100%"
            flex="1"
          >
            <Typography variant={isMobile ? 'h3' : 'h2'} pt={isMobile ? '12px' : '20px'} mb={0}>
              Déclaré
            </Typography>
            <Divider type="horizontal" mt={2} mb={3} />

            <Dl dtTextAlign="left" ddTextAlign="left" mb={isMobile ? 2 : 0}>
              <Dt>Raison Sociale</Dt>
              <Dd>{dataDeclare?.raisonSociale ?? undefDataValue}</Dd>
              <Dt>Siret</Dt>
              <Dd>{dataDeclare?.siret ?? undefDataValue}</Dd>
            </Dl>
          </Box>
        </Box>
      </Box>

      {/* Séparateur vertical - masqué sur mobile */}
      {!isMobile && <Divider h="100" pt={3} pb={0} m="10px 16px" />}

      {/* Colonne droite - Déclaration */}
      <Box
        marginTop={isMobile ? '0' : '10px'}
        paddingTop={isMobile ? '8px' : '16px'}
        flex="1"
        borderRadius="borderRadius000"
        maxHeight={isMobile ? 'none' : '600px'}
      >
        <Box
          className="tile-with-custom-radius"
          backgroundColor="var(--colorsUtilityYang100)"
          paddingLeft={isMobile ? 'var(--spacing050)' : 'var(--spacing100)'}
          paddingRight={isMobile ? 'var(--spacing150)' : 'var(--spacing300)'}
          boxSizing="border-box"
          borderRadius="borderRadius050"
          pl={isMobile ? '12px' : '20px'}
          display="flex"
          flexDirection="column"
          position="relative"
          height={isMobile ? 'auto' : '100%'}
          maxHeight={isMobile ? 'none' : '100%'}
          width="100%"
          overflow="auto"
        >
          <Typography variant={isMobile ? 'h3' : 'h2'} pt={isMobile ? '12px' : '20px'} mb={0}>
            Déclaration
          </Typography>
          <Divider type="horizontal" mt={2} mb={3} />

          <Dl dtTextAlign="left" ddTextAlign="left" mb={isMobile ? 2 : 0}>
            <Dt>Numéro d&apos;envoi</Dt>
            <Dd>{dataDeclaration?.numeroEnvoi ?? undefDataValue}</Dd>
            <Dt>Nature</Dt>
            <Dd>
              {dataDeclaration?.nature
                ? mapNatureToDisplay(dataDeclaration?.nature)
                : undefDataValue}
            </Dd>
            <Dt>Type</Dt>
            <Dd>
              {dataDeclaration?.type
                ? mapTypeLabelToDisplay(dataDeclaration?.type)
                : undefDataValue}
            </Dd>
            <Dt>Phase</Dt>
            <Dd>{dataDeclaration?.phase ?? undefDataValue}</Dd>
            <Dt>Mode d&apos;envoi</Dt>
            <Dd>{dataDeclaration?.modeEnvoi === true ? 'Test' : 'Réel'}</Dd>
            <Dt>Période</Dt>
            <Dd>{dataDeclaration?.periode ?? undefDataValue}</Dd>
            <Dt>Déposé le</Dt>
            <Dd>
              {dataDeclaration?.deposeLe
                ? formatDateString(dataDeclaration?.deposeLe, {
                    type: DateFormatType.DATETIME,
                  })
                : undefDataValue}
            </Dd>
            <Dt>Actualisé le</Dt>
            <Dd>
              {dataDeclaration?.actualiseLe
                ? formatDateString(dataDeclaration?.actualiseLe, {
                    type: DateFormatType.DATETIME,
                  })
                : undefDataValue}
            </Dd>
            <Dt>Statut</Dt>
            <Dd>
              {dataDeclaration?.statut ? (
                <Pill
                  variant={mapStatusDetailUIItem(dataDeclaration.statut).pillVariant}
                  size="S"
                  fill
                  ml="-8px"
                >
                  {mapStatusDetailUIItem(dataDeclaration.statut).label}
                </Pill>
              ) : (
                undefDataValue
              )}
            </Dd>
            {dataDeclaration?.salarie && (
              <>
                <Dt>Salarié</Dt>
                <Dd>{dataDeclaration.salarie}</Dd>
              </>
            )}
          </Dl>
        </Box>
      </Box>
    </Box>
  );
};

export default ApercuTab;

/* eslint-enable @typescript-eslint/no-deprecated */
